import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDownIcon } from "lucide-react"
import { siteConfig } from "@/components/homepage/_config"
import { CornerPlus } from "@/components/ui/corner-plus"
import { cn } from "@/lib/utils"

function Accordion({
    className,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
    return (
        <AccordionPrimitive.Root
            className={cn("w-full", className)}
            {...props}
        />
    )
}

function AccordionItem({
    className,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
    return (
        <AccordionPrimitive.Item
            className={cn("", className)}
            {...props}
        />
    )
}

function AccordionTrigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                className={cn(
                    "flex flex-1 items-center justify-between py-5 text-left font-medium transition-all [&[data-state=open]>svg]:rotate-180 cursor-pointer",
                    className,
                )}
                {...props}
            >
                {children}
                <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    )
}

function AccordionContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    return (
        <AccordionPrimitive.Content
            className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
            {...props}
        >
            <div className={cn("pb-5 pt-0", className)}>{children}</div>
        </AccordionPrimitive.Content>
    )
}

export function FAQ() {
    const { faqSection } = siteConfig

    return (
        <section id="faq" className="w-full relative">
            <div className="mx-auto">
                {/* Centered header */}
                <div className="text-center px-6 md:px-10 lg:px-14 pt-14 md:pt-20 pb-10">
                    <h3 className="text-3xl font-medium tracking-tighter md:text-4xl">
                        {faqSection.title}
                    </h3>
                    <p className="mt-4 text-muted-foreground text-balance max-w-md mx-auto">
                        {faqSection.description}
                    </p>
                </div>

                {/* FAQ grid — numbered rows with left index column */}
                <div className="border-t border-border">
                    <Accordion type="single" collapsible className="w-full">
                        {faqSection.faQitems.map((faq, index) => (
                            <AccordionItem
                                key={faq.id}
                                value={index.toString()}
                                className="border-b border-border"
                            >
                                <div className="relative grid grid-cols-[60px_1fr] md:grid-cols-[100px_1fr] divide-x divide-border">
                                    <CornerPlus position="top-right" className="text-muted-foreground/50" />
                                    {/* Number column */}
                                    <div className="flex items-start justify-center pt-5">
                                        <span className="text-sm font-mono text-muted-foreground tabular-nums">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    {/* Question + answer column */}
                                    <div className="px-5 md:px-8">
                                        <AccordionTrigger className="text-left no-underline hover:no-underline text-base">
                                            {faq.question}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-muted-foreground max-w-2xl">
                                            <p className="leading-relaxed">{faq.answer}</p>
                                        </AccordionContent>
                                    </div>
                                </div>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    )
}
